import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Navigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { TextLink } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { NewApplicationLayout } from '../../Organisms';
import EditApplicationGuard from '../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { useCurrentApplication } from '../../../services/hooks';
import { useGetCertificateName } from '../../../services/hooks/useGetCertificateName';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { getRouteForLanguage } from '../../../helpers/languageHelpers';
import { routes } from '../../../constants/routes';
import { legacyFallbackSearch } from './certreqRoutes';
import { applicationEngine } from './certreqEngine';
import { CERTREQ_ENGINE } from '../../../services/certreq/validationEngine';
import { useCertReqValidation } from '../../../services/certreq/useCertReqValidation';
import {
  confirmedRequirementIds,
  declarationsSatisfied,
  hasDeclarationRequirements,
  isRequirementConfirmed,
} from '../../../services/certreq/declarations';
import { ageRequirementSatisfied } from '../../../services/certreq/overrule';
import { useUserView } from '../../../services/certreq/userView/useUserView';
import { UserRequirementsView } from './UserRequirementsView';
import { UserRequirementsSkeleton } from './UserRequirementsSkeleton';
import { RequirementOverrule, useCertReqOverrule } from './RequirementOverrule';
import { ApplicationStatus } from '../../../types';

/**
 * Kravsiden for søknader som vurderes av kravmotoren (certreq).
 *
 * Kravtreet hentes fra `validate-me`; identiteten tas fra tokenet. Finnes ikke
 * kravkoden publisert (404), faller vi tilbake til den gamle kvalifikasjonssiden.
 *
 * **Siden vokter motoren selv.** `QualificationRouteSwitch` står bare på
 * `steps/qualifications` og slår derfor ikke inn på noen som ALLEREDE er her:
 * en bokmerket lenke, eller en frisk lasting av siden brukeren står på, gikk
 * rett forbi veksleren og viste den nye siden selv om backend hadde skrudd
 * søknaden tilbake til `legacy`. Nødbremsen må virke der brukeren faktisk er,
 * så vakten under sender hen tilbake til veksleren når motoren ikke er certreq.
 *
 * Siden viser kravene i **søkervisningen**: hva som må oppfylles, hvilke
 * alternative kvalifiseringsveier som finnes, og hvor dokumentasjonen skal
 * lastes opp. Den har med vilje ingen samlet statusflate over kravene —
 * «Mangler» på hele sertifikatet er ikke noe søkeren kan gjøre noe med, og
 * statusen står på hver enkelt rad.
 */
export const CertReqRequirementsPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const { applicationUid: routeUid } = useParams();
  const { application } = useCurrentApplication();

  /**
   * Motoren søknaden på denne ruta skal vurderes av — `undefined` så lenge vi
   * ikke vet. Samme kilde og samme uid-håndtering som veksleren bruker.
   */
  const engine = applicationEngine(application.result, routeUid);
  const isCertReq = engine === CERTREQ_ENGINE;

  const certificateCode = application.result?.certificate || '';
  /*
   * Motoren kjøres bare for søknader som faktisk er certreqs. Vet vi at denne
   * ikke er det, er vi på vei bort fra siden, og et `validate-me`-kall ville i
   * beste fall vært bortkastet — i verste fall svart 404 og utløst
   * `?legacy=1`-omdirigeringen før vakten under rakk å slå inn. En tom kode
   * gjør ingenting i hooken.
   */
  const { validation, reload } = useCertReqValidation(isCertReq ? certificateCode : '');
  // Kodeverket kan mangle et navn (eller ikke være lastet ennå); da står koden
  // i overskriften i stedet, så setningen aldri blir stående halv.
  const certificateName = useGetCertificateName(certificateCode) || certificateCode;

  const applicationUid = application.result?.uid || '';

  /** Har søkeren trykket «Neste» og blitt stående? Da skal hun få vite hvorfor. */
  const [hasClickedNext, setHasClickedNext] = useState(false);

  const handleNavBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const result = validation.result?.result;
  const confirmations = application.result?.confirmedQualifications;
  /*
   * Erklæringen kan bare avgis i kladd — `QualificationConfirmationController`
   * krever `MustBeInState(Draft)`, og avkryssingsboksen er låst ellers.
   */
  const isDraft = application.result?.applicationStatus === ApplicationStatus.Draft;

  /*
   * «Neste» sperres på ERKLÆRINGENE, og bare på dem.
   *
   * Den gamle kvalifikasjonssiden sperret til søkeren hadde et komplett
   * kvalifikasjonssett; certreq-siden sperrer med vilje ikke på dokumentasjon —
   * søkeren kan gå videre og laste opp senere. Erklæringen er noe annet: den er
   * en handling søkeren selv utfører her og nå, med straffelovhenvisning, og
   * teksten skal inn i det arkiverte søknadsdokumentet. Regelen speiler derfor
   * legacy for nettopp denne kravtypen — se `declarationsSatisfied()`.
   *
   * Krokene står FØR 404-returen under: de skal kjøres like mange ganger hver
   * gang komponenten tegnes.
   */
  const confirmedRequirements = useMemo(
    () => confirmedRequirementIds(confirmations, certificateCode),
    [confirmations, certificateCode]
  );

  /**
   * Sperrer erklæringene «Neste»?
   *
   * Tre avveininger, i denne rekkefølgen:
   *
   * 1. **Er ikke søknaden en kladd, sperrer ingenting.** Da KAN erklæringen
   *    ikke avgis, og en sperre blir en blindvei. Det er ikke et tenkt tilfelle:
   *    en søknad som ble påbegynt før certreq ble slått på, bærer de gamle
   *    TopBraid-URI-ene i `confirmedQualifications`. De matcher ingen
   *    certreq-nøkkel, så boksene står tomme — og er søknaden samtidig ute av
   *    kladd, ville søkeren stått fast for godt.
   * 2. **Har vi ikke kravtreet, sperrer vi.** `validate-me` kan feile, og et
   *    manglende tre er ikke det samme som ingen erklæringer: uten sperre kunne
   *    søkeren sendt inn et sjøfartskort helt uten erklæring, og
   *    «Alternativer»-seksjonen i det arkiverte søknadsdokumentet ville stått
   *    tom. Vi sperrer altså der vi ikke VET, og sier fra med en egen beskjed og
   *    en lenke som henter kravene på nytt.
   * 3. **Vet vi at treet ikke har erklæringer, slipper vi gjennom.** Alle andre
   *    sertifikater enn sjøfartskortet er dermed upåvirket.
   */
  const declarationsBlockNext = useMemo(() => {
    if (!isDraft) return false;
    if (!result) return true;
    if (!hasDeclarationRequirements(result.root)) return false;
    return !declarationsSatisfied(result.root, (requirementId) =>
      isRequirementConfirmed(confirmedRequirements, requirementId)
    );
  }, [isDraft, result, confirmedRequirements]);

  /*
   * Søkervisningen av kravtreet, med søknadens vedlegg og erklæringer lagt
   * oppå. Siden trenger den samlede statusen, ikke selve nodene: det er den som
   * avgjør om noe fortsatt mangler dokumentasjon.
   */
  const view = useUserView(result, certificateName, certificateCode);
  const overrule = useCertReqOverrule();

  /*
   * Plassholderen står også mens motoren er uavgjort: da har vi verken kravene
   * eller grunnlag for å sende brukeren bort, og flaten skal ikke stå tom i
   * mellomtiden. Feilet hentingen av søknaden, blir motoren aldri avgjort — da
   * viker plassholderen for feilmeldingen, slik den alltid har gjort.
   */
  const showLoader = application.loading || validation.loading || (!engine && !application.error);

  /**
   * Mangler det dokumentasjon?
   *
   * «Dokumentert» teller som levert — motoren har ikke sett fila ennå, men
   * søkeren har gjort sitt, og det er nøyaktig regelen den gamle
   * kvalifikasjonssiden brukte (`allBaseItemsOk && anyAlternativeOk`, der et
   * vedlegg gjør kortet gyldig). Uten kravtre vet vi ingenting, og da sperrer vi
   * ikke: erklæringssperren over dekker allerede det tilfellet.
   */
  const documentationMissing = !!view && view.status === 'missing';

  /*
   * Sjøfartskortet kan ikke overprøves — der er det erklæringene som avgjør, og
   * den gamle siden holdt overprøvingen unna SID av samme grunn.
   */
  const isSid = ['SID', 'SID_duplicate'].includes(certificateCode);

  /**
   * Skal overprøvingen tilbys?
   *
   * Samme fire betingelser som på den gamle kvalifikasjonssiden: vi må ha
   * kravene, noe må faktisk mangle, alderskravet må være i orden (alder kan
   * ingen saksbehandler overprøve), og sertifikatet må ikke være sjøfartskortet.
   */
  const showOverrule =
    !showLoader && documentationMissing && ageRequirementSatisfied(result?.root) && !isSid;

  /**
   * «Neste» — med overprøvingen som veto over dokumentasjonskravet.
   *
   * Rekkefølgen er den gamle sidens: overprøvingen lagres først, og har søkeren
   * krysset av, går hun videre uten at manglende dokumentasjon stopper henne.
   * Er haken borte, ryddes en tidligere overprøving bort, og da er det
   * dokumentasjonen som avgjør — mangler noe, blir hun stående med beskjeden om
   * hvorfor.
   */
  const handleNavNext = useCallback(async () => {
    if (showOverrule) {
      const saved = await overrule.save();
      if (overrule.checked) {
        // Lagringen feilet, eller kommentaren mangler — seksjonen sier fra selv.
        if (saved) {
          navigate(generatePath(routes.application.contactInfo, { applicationUid }));
        }
        return;
      }
    }

    if (documentationMissing) {
      setHasClickedNext(true);
      return;
    }

    navigate(generatePath(routes.application.contactInfo, { applicationUid }));
  }, [applicationUid, documentationMissing, navigate, overrule, showOverrule]);

  /*
   * Motorvakten. Søknaden er hentet, og den hører ikke hjemme her — send
   * brukeren tilbake til `steps/qualifications`, som er veksleren. UTEN
   * `?legacy=1`: parameteren er 404-fallbackens egen nødutgang, og her er det
   * veksleren som skal avgjøre på nytt. Den vil lese den samme motoren og vise
   * den gamle siden.
   *
   * Ingen løkke: veksleren navigerer HIT bare når motoren er certreq, og vakten
   * herfra bare når den ikke er det. De to kan aldri være sanne samtidig.
   */
  if (engine && !isCertReq) {
    const switchPath = generatePath(routes.application.qualification, {
      applicationUid: routeUid || applicationUid,
    });
    return <Navigate to={getRouteForLanguage(intl.locale, switchPath)} replace />;
  }

  // 404: kravkoden finnes ikke publisert (f.eks. *_duplicate-koder).
  // Da er den gamle siden riktig svar — ikke en feilside. Vi sender brukeren
  // tilbake til den gamle ruta med ?legacy=1, slik at hen står på riktig URL.
  if (validation.error?.kind === 'notfound') {
    const legacyPath = generatePath(routes.application.qualification, { applicationUid });
    const legacyTarget = `${getRouteForLanguage(intl.locale, legacyPath)}${legacyFallbackSearch}`;
    return <Navigate to={legacyTarget} replace />;
  }

  return (
    <EditApplicationGuard>
      <NewApplicationLayout
        mainContentMaxWidth="100%"
        canNavBack
        canNavNext={!declarationsBlockNext && !overrule.saving}
        exitLabel={intl.formatMessage({ id: 'general.form.back' })}
        navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
        navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
        onNavNextClick={handleNavNext}
        onNavBackClick={handleNavBack}
      >
        <Wrapper>
          {/*
            Kravtreet tar noen sekunder — `validate-me` slår opp i flere
            kilderegistre før motoren kjøres. I stedet for en spinner på tom
            flate står plassholderen som viser hva som kommer.
          */}
          {showLoader && <UserRequirementsSkeleton />}

          {application.error && <ErrorMessage error={application.error} />}

          {validation.error?.kind === 'forbidden' && (
            <ErrorWrapper>
              <ErrorMessage
                title={intl.formatMessage({ id: 'certreq.error.forbidden.title' })}
                error={intl.formatMessage({ id: 'certreq.error.forbidden.desc' })}
              />
            </ErrorWrapper>
          )}

          {validation.error?.kind === 'gateway' && (
            <ErrorWrapper>
              <ErrorMessage
                title={intl.formatMessage({ id: 'certreq.error.gateway.title' })}
                error={
                  validation.error.message ||
                  intl.formatMessage({ id: 'certreq.error.gateway.desc' })
                }
              />
            </ErrorWrapper>
          )}

          {validation.error?.kind === 'unknown' && (
            <ErrorWrapper>
              <ErrorMessage
                title={intl.formatMessage({ id: 'certreq.error.unknown.title' })}
                error={
                  validation.error.message ||
                  intl.formatMessage({ id: 'certreq.error.unknown.desc' })
                }
              >
                <TextLink
                  text={intl.formatMessage({ id: 'general.button.retry' })}
                  onClick={reload}
                />
              </ErrorMessage>
            </ErrorWrapper>
          )}

          {result && (
            <Requirements>
              <UserRequirementsView
                result={result}
                certificateName={certificateName}
                certificateCode={certificateCode}
              />
              {/*
                En låst «Neste»-knapp uten forklaring er en blindvei. Beskjeden
                står bare når det faktisk er en erklæring som mangler — og som
                søkeren kan gjøre noe med, altså i kladd.
              */}
              {declarationsBlockNext && (
                <DeclarationNotice data-testid="certreq-declaration-required">
                  {intl.formatMessage({ id: 'certreq.userview.declaration.required' })}
                </DeclarationNotice>
              )}

              {/*
                «Systemet er uenig med deg?» — samme spørsmål, samme tekst og
                samme lagring som på den gamle kvalifikasjonssiden.
              */}
              {showOverrule && <RequirementOverrule overrule={overrule} />}

              {/*
                Søkeren trykket «Neste» og ble stående. Beskjeden er den gamle
                sidens, ordrett: «Mangler dokumentasjon — et eller flere av
                kravene må dokumenteres.» Den forsvinner av seg selv så snart
                dokumentasjonen er på plass, eller overprøvingen er krysset av.
              */}
              {hasClickedNext && documentationMissing && !overrule.checked && (
                <ErrorWrapper data-testid="certreq-documentation-missing">
                  <ErrorMessage
                    title={intl.formatMessage({ id: 'app.requirements.missing.error.title' })}
                    error={intl.formatMessage({ id: 'app.requirements.missing.error.desc' })}
                  />
                </ErrorWrapper>
              )}
            </Requirements>
          )}

          {/*
            Samme beskjed for det andre tilfellet: vi har ikke kravtreet, og vet
            derfor ikke om det finnes en erklæring å avgi. Lenken henter kravene
            på nytt, slik at sperren ikke blir stående av et hikk i nettet.
          */}
          {!result && !showLoader && declarationsBlockNext && (
            <DeclarationNotice data-testid="certreq-declaration-unknown">
              {intl.formatMessage({ id: 'certreq.userview.declaration.unknown' })}{' '}
              <TextLink
                text={intl.formatMessage({ id: 'general.button.retry' })}
                onClick={reload}
              />
            </DeclarationNotice>
          )}
        </Wrapper>
      </NewApplicationLayout>
    </EditApplicationGuard>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const Requirements = styled.div`
  margin-bottom: 4rem;
`;

/**
 * Beskjeden som forklarer den låste «Neste»-knappen.
 *
 * Den står nederst, rett over knapperaden, fordi det er der blikket havner når
 * knappen ikke gir etter. Formen er den samme dempede varselflaten som resten av
 * siden bruker — dette er ikke en feil søkeren har gjort, det er et steg hun
 * ikke har tatt ennå.
 */
const DeclarationNotice = styled.p`
  margin: 1.2rem 0 0;
  padding: 0.8rem 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.alert.warningDark};
  background: ${({ theme }) => theme.colors.alert.warningLight};
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.font.text};
`;

const ErrorWrapper = styled.div`
  max-width: 600px;
  margin-top: 20px;
`;

export default CertReqRequirementsPage;
